'use client';

import PraxisLogo from '@/assets/praxis-brand-logo.png';
import Image from 'next/image';
import Button from '@/ui/Button';
import React from 'react';
import { useRouter } from 'next/navigation';
import { handleSignOut } from '@/app/lib/actions';
import posthog from 'posthog-js';

interface NavButtonProps {
	text: string;
	onClick: () => void;
	beta: boolean;
}

export const BetaTag = () => (
	// eslint-disable-next-line prettier/prettier
	<span className="ml-1 inline-flex items-center rounded-md bg-badge-dark px-2 py-0.5 text-label-small text-primary-light">
		BETA
	</span>
);

const NavButtonGroup = ({ buttons }: { buttons: NavButtonProps[] }) => (
	<div className="ml-auto flex items-center">
		{buttons.map((button) => (
			<Button
				key={button.text}
				variant="contained"
				environment="light"
				classOverrides="!text-grey-800 h-7 !bg-transparent hover:!text-grey-600 "
				onClick={() => {
					button.onClick();
				}}
			>
				<div className="flex items-center">
					{button.text}
					{button.beta && <BetaTag />}
				</div>
			</Button>
		))}
	</div>
);

export default function TopBar({ buttons }: { buttons: boolean }) {
	const router = useRouter();

	const onSignOut = async () => {
		try {
			await handleSignOut();
			posthog.reset();
			posthog.capture('topbar_logout_button_clicked');
		} catch (error) {
			console.error('Error signing out:', error);
			console.error(error);
		}
	};

	const NavButtons = [
		{
			text: 'Home',
			beta: false,
			onClick: () => {
				posthog.capture('topbar_home_button_clicked');
				router.push('/home');
			},
		},
		{
			text: 'Reports',
			beta: false,
			onClick: () => {
				posthog.capture('topbar_reports_button_clicked');
				router.push('/reports');
			},
		},
		{
			text: 'My Coach',
			beta: true,
			onClick: () => {
				posthog.capture('topbar_my_coach_button_clicked');
				router.push('/coach');
			},
		},
		{
			text: 'Logout',
			beta: false,
			onClick: onSignOut,
		},
	];

	return (
		<header className="fixed left-0 right-0 top-0 z-50 flex h-7 items-center bg-white-000 px-6">
			<Button
				variant="contained"
				environment="light"
				classOverrides="!bg-transparent"
				onClick={() => router.push('/home')}
			>
				<Image alt="Praxis Labs" src={PraxisLogo} width={236} height={16} priority />
			</Button>
			{buttons && <NavButtonGroup buttons={NavButtons} />}
		</header>
	);
}
